import React, { useCallback, useEffect, useId, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Modal.css';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDestructive?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isDestructive = false,
}) => {
  const titleId = useId();
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Capture the element that had focus before the dialog opened so we can
  // return focus when it closes (a11y best practice).
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current = (document.activeElement as HTMLElement) || null;
      // Focus the safe default action — Cancel for destructive dialogs,
      // Confirm otherwise.
      const target = isDestructive ? cancelButtonRef.current : confirmButtonRef.current;
      target?.focus();
    } else if (previouslyFocusedRef.current) {
      previouslyFocusedRef.current.focus();
      previouslyFocusedRef.current = null;
    }
  }, [isOpen, isDestructive]);

  // Keyboard handling: ESC dismisses; Tab is constrained to the two buttons
  // so focus can't escape behind the modal.
  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCancel();
      return;
    }
    if (e.key === 'Tab') {
      const cancelEl = cancelButtonRef.current;
      const confirmEl = confirmButtonRef.current;
      if (!cancelEl || !confirmEl) return;
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === cancelEl) {
          e.preventDefault();
          confirmEl.focus();
        }
      } else {
        if (active === confirmEl) {
          e.preventDefault();
          cancelEl.focus();
        }
      }
    }
  }, [onCancel]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onCancel}
          />
          <motion.div
            className="confirmation-modal"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onKeyDown={onKeyDown}
          >
            <div className="modal-header">
              <h3 className="modal-title" id={titleId}>{title}</h3>
            </div>

            <div className="modal-body">
              <p className="modal-message">{message}</p>
            </div>

            <div className="modal-actions">
              <button
                ref={cancelButtonRef}
                className="modal-button modal-button-secondary"
                type="button"
                onClick={onCancel}
              >
                {cancelText}
              </button>
              <button
                ref={confirmButtonRef}
                className={`modal-button ${isDestructive ? 'modal-button-destructive' : 'modal-button-primary'}`}
                type="button"
                onClick={onConfirm}
              >
                {confirmText}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ConfirmationModal;
